import { SOL_LOGO, USDC_LOGO, USDT_LOGO } from '@root/shared/assets'

/**
 * The assets a transfer can be in.
 *
 * Only these are indexed — the transfers table holds numéraires alone — so
 * this is the whole set rather than a convenience for the common ones. An
 * amount without its asset is not a figure a reader can use: 0.0006 of what.
 *
 * `kind` is how much can be said about the money's worth. A stablecoin is its
 * own dollar figure; the native coin needs today's price; anything else we
 * would be guessing at, and a guess drawn as a dollar figure reads as fact.
 *
 * A mint that is not here means the indexer started keeping something new and
 * this was not updated. Showing the address is wrong but not misleading,
 * which is the right way round.
 */
export type Numeraire = {
  ticker: string
  logo: string | undefined
  kind: 'native' | 'stable' | 'unknown'
}

const NUMERAIRES: Record<string, Numeraire> = {
  So11111111111111111111111111111111111111112: {
    ticker: 'SOL',
    logo: SOL_LOGO,
    kind: 'native',
  },
  // Lamports moved by the system program itself, which is SOL that was never
  // wrapped. The indexer records the program as the mint because there is no
  // other address to record; measured on staging, it is one row in 300.
  '11111111111111111111111111111111': {
    ticker: 'SOL',
    logo: SOL_LOGO,
    kind: 'native',
  },
  EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v: {
    ticker: 'USDC',
    logo: USDC_LOGO,
    kind: 'stable',
  },
  Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB: {
    ticker: 'USDT',
    logo: USDT_LOGO,
    kind: 'stable',
  },
  USD1ttGY1N17NEEHLmELoaybftRBUSErhqYiQzvEmuB: {
    ticker: 'USD1',
    logo: undefined,
    kind: 'stable',
  },
}

export const numeraireOf = (mint: string): Numeraire =>
  NUMERAIRES[mint] ?? {
    ticker: `${mint.slice(0, 4)}…${mint.slice(-4)}`,
    logo: undefined,
    kind: 'unknown',
  }

export const tickerOf = (mint: string): string => numeraireOf(mint).ticker

/**
 * What the moved amount is worth, or nothing when we cannot say.
 *
 * Today's price, not the price when it moved — the transfers we keep go back
 * days, not months, so the two are close, and the column says which it is.
 * Undefined rather than zero for an asset we hold no price for: a zero in a
 * money column reads as "worthless".
 */
export const valueInUsd = (
  mint: string,
  amount: number,
  nativePriceUsd: number,
): number | undefined => {
  const { kind } = numeraireOf(mint)
  if (kind === 'stable') {
    return amount
  }
  if (kind === 'native' && nativePriceUsd > 0) {
    return amount * nativePriceUsd
  }
  return undefined
}
